import type { ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { DEFAULT, match } from '@/utils/pattern-match-util'
import { dateFormatters } from '@/utils/date-util'
import { Button } from '@/components/Button'
import { StyledLink } from '@/components/Link'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import type { BookkeepingJobDetailsViewModel } from '@/features/jobs/details/bookkeeping-type/BookkeepingJobDetailsViewModel'
import {
  useDefaultHeaderContent,
  getPendingJobScopeChangeElements,
  getRatingsElements,
  PageHeader,
  ScrolledAction,
} from '@/features/jobs/details/components/PageHeader'
import { ActionCard } from '@/features/jobs/details/components/ActionCard'
import {
  ExpectationsDialog,
  useExpectationsDialog,
} from '@/features/jobs/details/components/ExpectationsDialog'
import { Step, StepList } from '@/components/StepList'

export interface BookkeepingJobDetailsHeaderProps {
  bookkeepingJobVm: BookkeepingJobDetailsViewModel
  jobVm: JobDetailsViewModel
}

/**
 * The BookkeepingJobDetailsHeader
 */
export const BookkeepingJobDetailsHeader = observer(
  function BookkeepingJobDetailsHeader({
    bookkeepingJobVm,
    jobVm,
  }: BookkeepingJobDetailsHeaderProps) {
    const bookkeepingProgressionInfo =
      bookkeepingJobVm.bookkeepingProgressionInfo

    const bookkeepingDisplayInfo = getBookkeepingDisplayInfo(bookkeepingJobVm)
    const { title, description } = useDefaultHeaderContent(jobVm, {
      bookkeeping: bookkeepingProgressionInfo?.displayStage,
    })
    const { scrolledAction, actionCard } = getBookkeepingElements(
      jobVm,
      bookkeepingDisplayInfo,
    )

    const {
      isOpen: isExpectationsDialogOpen,
      open: openExpectationsDialog,
      close: closeExpectationsDialog,
    } = useExpectationsDialog()

    return (
      <>
        <PageHeader
          vm={jobVm}
          headline={title}
          description={
            <>
              {description}{' '}
              <StyledLink onPress={openExpectationsDialog}>
                {bookkeepingProgressionInfo?.displayStage ===
                'BookkeepingCompleted'
                  ? "What's done"
                  : "What's next"}
              </StyledLink>
            </>
          }
          scrolledHeadline={title}
          isSkeleton={
            bookkeepingJobVm.bookkeepingVm.fetchBookkeepingProgressionInfo
              .pending
          }
          actionCard={actionCard}
          scrolledAction={scrolledAction}
        />
        <BookkeepingJobExpectationsDialog
          isOpen={isExpectationsDialogOpen}
          onClose={closeExpectationsDialog}
          vm={bookkeepingJobVm}
          testId="expectations-dialog"
          confirmTestId="bookkeeping-expectations-dialog-close-button"
        />
      </>
    )
  },
)

interface BookkeepingJobExpectationsDialogProps {
  vm: BookkeepingJobDetailsViewModel
  isOpen: boolean
  onClose: () => unknown
  testId?: string
  confirmTestId?: string
}

/**
 * Bookkeeping Job Expectations Dialog
 */
export const BookkeepingJobExpectationsDialog = observer(
  function BookkeepingJobExpectationsDialog({
    vm,
    isOpen,
    onClose,
    testId,
    confirmTestId,
  }: BookkeepingJobExpectationsDialogProps) {
    return (
      <ExpectationsDialog
        isOpen={isOpen}
        onClose={onClose}
        testId={testId}
        confirmTestId={confirmTestId}
      >
        <StepList>
          {vm.expectationSteps.map((step, index) => (
            <Step
              key={index}
              step={index + 1}
              title={step.title}
              state={
                step.isCompleted
                  ? 'completed'
                  : step.isCurrent
                    ? 'current'
                    : 'pending'
              }
            >
              {step.description}
            </Step>
          ))}
        </StepList>
      </ExpectationsDialog>
    )
  },
)

/**
 * Composes the bookkeeping action and scrolled action for the header.
 */
function getBookkeepingElements(
  vm: JobDetailsViewModel,
  display: BookkeepingDisplayInfo | null,
): {
  scrolledAction?: ReactNode
  actionCard?: ReactNode
} {
  const pendingJobScopeChangeElements = getPendingJobScopeChangeElements(vm)

  if (pendingJobScopeChangeElements) {
    return pendingJobScopeChangeElements
  }

  const ratingsElements = getRatingsElements(vm)

  if (ratingsElements) {
    return ratingsElements
  }

  if (!display) {
    return { scrolledAction: null, actionCard: null }
  }

  return {
    actionCard: (
      <ActionCard kicker={display.kicker} title={display.title}>
        {display.action}
      </ActionCard>
    ),
    scrolledAction: display.scrolledAction,
  }
}

interface BookkeepingDisplayInfo {
  kicker: string
  title: string
  action?: ReactNode
  scrolledAction?: ReactNode
}

/**
 * Returns the bookkeeping display info based on the current stage.
 */
function getBookkeepingDisplayInfo(
  bookkeepingJobVm: BookkeepingJobDetailsViewModel,
) {
  const bookkeepingProgressionInfo = bookkeepingJobVm.bookkeepingProgressionInfo
  if (!bookkeepingJobVm.currentBookkeepingStep) {
    return null
  }

  return match(bookkeepingJobVm.currentBookkeepingStep?.stage, {
    PendingAccess: () => ({
      kicker: 'Link to your software',
      title: 'Access required',
      action: (
        <Button
          label="Connect your account"
          onPress={bookkeepingJobVm.bookkeepingVm.connectYourAccount}
          testId="connect-account-button"
        />
      ),
      scrolledAction: <ScrolledAction label="Connect your account" />,
    }),
    BookkeepingCompleted: () => {
      return {
        kicker: 'Bookkeeping completed',
        title: dateFormatters.dateLong.format(
          bookkeepingProgressionInfo?.completedAt,
        ),
      }
    },
    [DEFAULT]: () => null,
  })
}
